// packages/app/src/pages/ApiDocsIndexPage.tsx
import React from 'react';
import { makeStyles } from '@material-ui/core';
import {
  EntityListProvider,
  CatalogFilterLayout,
  EntitySearchBar,
  EntityKindPicker,
  EntityLifecyclePicker,
  EntityOwnerPicker,
  EntityRefLink,
} from '@backstage/plugin-catalog-react';
import { CatalogTable, CatalogTableRow } from '@backstage/plugin-catalog';
import type { TableColumn } from '@backstage/core-components';
import { Content, Header, Page } from '@backstage/core-components';
import { ApiRoutablePicker } from '../components/apiDocs/ApiRoutablePicker';

const useStyles = makeStyles({
  header: {
    backgroundColor: '#162746 !important',
    backgroundImage: 'none !important',
    '&::before, &::after': {
      background: 'transparent !important',
      backgroundImage: 'none !important',
    },
    '& [class*="BackstageHeader"], & [class*="PageTheme"], & svg': {
      background: '#162746 !important',
      backgroundImage: 'none !important',
    },
    '& svg': {
      display: 'none !important',
    },
  },
});

const toComparable = (value: unknown): string => String(value ?? '').toLowerCase();

const compareBy = (
  a: CatalogTableRow,
  b: CatalogTableRow,
  selector: (row: CatalogTableRow) => unknown,
): number => {
  const left = toComparable(selector(a));
  const right = toComparable(selector(b));
  return left.localeCompare(right);
};

export const ApiDocsIndexPage = () => {
  const classes = useStyles();
  const getName = ({ entity }: CatalogTableRow) => entity.metadata.name ?? '';
  const getSystem = ({ entity }: CatalogTableRow) =>
    entity.relations?.find(r => r.type === 'partOf')?.targetRef ?? '';
  const getOwner = ({ entity }: CatalogTableRow) =>
    (entity as any).spec?.owner ?? '';
  const getRoute = ({ entity }: CatalogTableRow) =>
    entity.metadata.labels?.['api-routable'] ?? '';
  const getLifecycle = ({ entity }: CatalogTableRow) =>
    (entity as any).spec?.lifecycle ?? '';
  const getDescription = ({ entity }: CatalogTableRow) =>
    entity.metadata.description ?? '';
  const getTags = ({ entity }: CatalogTableRow) =>
    entity.metadata.tags?.join(', ') ?? '';

  const columns: TableColumn<CatalogTableRow>[] = [
    // NAME
    {
      title: 'Name',
      field: 'entity.metadata.name',
      customSort: (a, b) => compareBy(a, b, getName),
      render: ({ entity }) => (
	<strong>
          <EntityRefLink entityRef={entity} defaultKind="API" />
	</strong>
      ),
    },

    // SYSTEM
    {
      title: 'System',
      field: 'entity.relations',
      customSort: (a, b) => compareBy(a, b, getSystem),
      render: ({ entity }) => {
        const system = entity.relations?.find(
          r => r.type === 'partOf',
        )?.targetRef;

        return system ? <EntityRefLink entityRef={system} /> : '—';
      },
    },

    // OWNER
    {
      title: 'Owner',
      field: 'entity.spec.owner',
      customSort: (a, b) => compareBy(a, b, getOwner),
      render: ({ entity }) => {
        const owner = (entity as any).spec?.owner;
        if (!owner) return '—';
    
        const ownerStr = String(owner);

      // Valid entityRef strings have at least a kind prefix like "group:" or "user:"
      if (!ownerStr.includes(':')) {
        return ownerStr; // plain text, no crash
      }

      return <EntityRefLink entityRef={ownerStr} />;
      },
    },

    // ✅ ROUTE (replaces TYPE)
    {
      title: 'Route',
      field: 'entity.metadata.labels.api-routable',
      customSort: (a, b) => compareBy(a, b, getRoute),
      render: ({ entity }) =>
        entity.metadata.labels?.['api-routable'] ?? '—',
    },

    // LIFECYCLE
    {
      title: 'Lifecycle',
      field: 'entity.spec.lifecycle',
      customSort: (a, b) => compareBy(a, b, getLifecycle),
      render: ({ entity }) =>
        (entity as any).spec?.lifecycle ?? '—',
    },

    // DESCRIPTION
    {
      title: 'Description',
      field: 'entity.metadata.description',
      customSort: (a, b) => compareBy(a, b, getDescription),
      render: ({ entity }) =>
        entity.metadata.description ?? '—',
    },

    // TAGS
    {
      title: 'Tags',
      field: 'entity.metadata.tags',
      customSort: (a, b) => compareBy(a, b, getTags),
      render: ({ entity }) =>
        entity.metadata.tags?.length
          ? entity.metadata.tags.join(', ')
          : '—',
    },
  ];

  return (
    <Page>
      <Header className={classes.header} title="All APIs" />
      <Content>
        <EntityListProvider initialFilters={{ kind: 'API' }}>
          <CatalogFilterLayout>
            <CatalogFilterLayout.Filters>
              <EntitySearchBar />
              <EntityKindPicker initialFilter="api" hidden />
              <EntityOwnerPicker />
              <EntityLifecyclePicker />
              <ApiRoutablePicker />
            </CatalogFilterLayout.Filters>

            <CatalogFilterLayout.Content>
              <CatalogTable columns={columns} />
            </CatalogFilterLayout.Content>
          </CatalogFilterLayout>
        </EntityListProvider>
      </Content>
    </Page>
  );
};
